import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { Calculator, X, Settings2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const QTYS = Array.from({ length: 49 }, (_, i) => +(0.4 + i * 0.2).toFixed(1)); // 0.4 → 10.0
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RATE_KEYS = [
  { key: "b_price", label: "B Price" },
  { key: "c_price", label: "C Price" },
  { key: "cash_price", label: "Cash Price" },
];

export default function PriceCalcPanel({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [smallLoad, setSmallLoad] = useState({ min_load_m3: 0, min_load_charge: 0 });
  const [rate, setRate] = useState("b_price");
  const [qty, setQty] = useState(0.4);
  const [calc, setCalc] = useState(null); // { zone, extraKm }
  const [askKm, setAskKm] = useState(false);
  const [kmInput, setKmInput] = useState("");
  const loaded = useRef(false);

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    api.get("/settings/pricing")
      .then(({ data }) => setSmallLoad(data || {}))
      .catch(() => {});
    api.get("/settings/price-calc")
      .then(({ data }) => { setCfg(data); setDraft(data); })
      .catch(() => {
        const fallback = { b_price: 0, c_price: 0, cash_price: 0, cartage_per_km: 0, gst_rate: 0.1, min_m3: 1, short_load_fee: 0 };
        setCfg(fallback); setDraft(fallback);
      });
  }, [open]);

  const result = useMemo(() => {
    if (!cfg || !calc) return null;
    const per = Number(cfg[rate]) || 0;
    const concrete = per * qty;
    const extraKm = calc.zone === "outside" ? Math.max(0, Number(calc.extraKm) || 0) : 0;
    const cartage = extraKm * (Number(cfg.cartage_per_km) || 0);
    // Small load — per m³ of shortfall under the minimum billed size
    // (e.g. 1.0 m³ ordered vs a 3 m³ minimum = 2 m³ × $70 = $140).
    const minM3 = Number(smallLoad.min_load_m3) || 0;
    const shortfallM3 = qty < minM3 ? +(minM3 - qty).toFixed(2) : 0;
    const shortLoad = +(shortfallM3 * (Number(smallLoad.min_load_charge) || 0)).toFixed(2);
    const ex = concrete + cartage + shortLoad;
    const gst = ex * (Number(cfg.gst_rate) || 0);
    return { per, concrete, extraKm, cartage, shortfallM3, minM3, shortLoad, ex, gst, inc: ex + gst };
  }, [cfg, calc, rate, qty, smallLoad]);

  const run = (zone) => {
    if (zone === "outside") { setKmInput(""); setAskKm(true); return; }
    setCalc({ zone: "inside", extraKm: 0 });
  };

  const confirmKm = () => {
    setCalc({ zone: "outside", extraKm: Number(kmInput) || 0 });
    setAskKm(false);
  };

  const clearAll = () => { setCalc(null); setAskKm(false); setKmInput(""); setQty(0.4); };

  const saveCfg = async () => {
    try {
      const { data } = await api.put("/settings/price-calc", {
        ...draft,
        gst_rate: (Number(draft.gst_rate) || 0) > 1 ? (Number(draft.gst_rate) / 100) : Number(draft.gst_rate) || 0,
      });
      setCfg(data); setDraft(data);
      if (smallLoad.delivery_rate_per_km !== undefined) {
        try {
          const { data: p } = await api.put("/settings/pricing", {
            ...smallLoad,
            min_load_m3: Number(smallLoad.min_load_m3) || 0,
            min_load_charge: Number(smallLoad.min_load_charge) || 0,
          });
          setSmallLoad(p);
        } catch (_) {
          toast.error("Small load settings need a manager or admin to save");
        }
      }
      setShowSettings(false);
      toast.success("Rates saved for your organisation");
    } catch (e) {
      toast.error("Could not save rates");
    }
  };

  const num = (k) => (
    <input
      type="number" step="0.01" inputMode="decimal"
      data-testid={`price-calc-input-${k.replace(/_/g, "-")}`}
      value={draft?.[k] ?? ""}
      onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
      className="w-full rounded-none bg-transparent border border-industrial px-2 py-1.5 mono text-sm text-ink focus:border-primary-orange outline-none"
    />
  );

  return (
    <>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="price-calc-open-btn"
        title="Quick price calculator (B / C / Cash rates, cartage, GST)"
        className={`mono text-[9px] uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1 ${
          open ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
        } ${className}`}>
        <Calculator className="w-3 h-3" /> Price Calc
      </button>

      {open && createPortal((
        <div data-testid="price-calc-panel"
          className="fixed z-50 bottom-4 right-4 w-[min(94vw,340px)] max-h-[82vh] overflow-y-auto border border-industrial bg-surface shadow-2xl">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-industrial bg-black/20 sticky top-0">
            <Calculator className="w-3.5 h-3.5 text-primary-orange" />
            <span className="heading text-xs font-black uppercase tracking-widest flex-1">Price Calc</span>
            <button type="button" onClick={() => setShowSettings((v) => !v)} data-testid="price-calc-settings-btn"
              title="Rate settings" className="text-muted-industrial hover:text-primary-orange">
              <Settings2 className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => setOpen(false)} data-testid="price-calc-close-btn"
              className="text-muted-industrial hover:text-primary-orange"><X className="w-4 h-4" /></button>
          </div>

          <div className="p-3 space-y-3">
            {showSettings ? (
              <div className="space-y-2" data-testid="price-calc-settings">
                {RATE_KEYS.map(({ key, label }) => (
                  <div key={key}>
                    <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">{label} · per m³</div>
                    {num(key)}
                  </div>
                ))}
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Cartage · per km beyond 25km</div>
                  {num("cartage_per_km")}
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="price-calc-small-load-settings">
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Minimum billed size (m³)</div>
                    <input type="number" step="0.1" inputMode="decimal" data-testid="price-calc-input-min-load-m3"
                      value={smallLoad.min_load_m3}
                      onChange={(e) => setSmallLoad({ ...smallLoad, min_load_m3: e.target.value })}
                      className="w-full rounded-none bg-transparent border border-industrial px-2 py-1.5 mono text-sm text-ink focus:border-primary-orange outline-none" />
                  </div>
                  <div>
                    <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Cartage shortfall ($/m³)</div>
                    <input type="number" step="0.01" inputMode="decimal" data-testid="price-calc-input-min-load-charge"
                      value={smallLoad.min_load_charge}
                      onChange={(e) => setSmallLoad({ ...smallLoad, min_load_charge: e.target.value })}
                      className="w-full rounded-none bg-transparent border border-industrial px-2 py-1.5 mono text-sm text-ink focus:border-primary-orange outline-none" />
                  </div>
                  <div className="col-span-2 mono text-[9px] text-muted-industrial leading-snug">
                    Charged per m³ of shortfall (e.g. 1.0 m³ vs 3 m³ min = 2 m³ × ${Number(smallLoad.min_load_charge) || 0} = ${((Number(smallLoad.min_load_charge) || 0) * 2).toFixed(0)}). Shared with the Pricing page.
                  </div>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">GST rate (0.10 = 10%)</div>
                  {num("gst_rate")}
                </div>
                <button type="button" onClick={saveCfg} data-testid="price-calc-save-btn"
                  className="w-full mono text-[10px] uppercase tracking-widest font-black px-2 py-2 bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] inline-flex items-center justify-center gap-1">
                  <Save className="w-3 h-3" /> Save Rates
                </button>
              </div>
            ) : (
              <>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Price Type · N25 / N32</div>
                  <div className="grid grid-cols-3 gap-1">
                    {RATE_KEYS.map(({ key, label }) => (
                      <button key={key} type="button" onClick={() => setRate(key)}
                        data-testid={`price-calc-rate-${key.replace(/_/g, "-")}`}
                        className={`border px-1 py-1.5 mono text-[9px] uppercase tracking-widest ${
                          rate === key ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-muted-industrial hover:text-ink"
                        }`}>
                        <div className="font-black">{label.replace(" Price", "")}</div>
                        <div className="text-[9px] opacity-70">{money(cfg?.[key])}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Quantity</div>
                  <div className="flex items-center gap-1">
                    <button type="button" data-testid="price-calc-qty-minus" onClick={() => setQty((q) => Math.max(0.4, +(q - 0.2).toFixed(1)))}
                      className="px-2 py-1.5 border border-industrial text-muted-industrial hover:text-primary-orange mono text-sm">−</button>
                    <select value={qty} data-testid="price-calc-qty-select"
                      onChange={(e) => setQty(Number(e.target.value))}
                      className="flex-1 rounded-none bg-transparent border border-industrial px-2 py-1.5 mono text-base font-black text-ink text-center outline-none focus:border-primary-orange">
                      {QTYS.map((q) => <option key={q} value={q} className="bg-surface">{q.toFixed(1)} m³</option>)}
                    </select>
                    <button type="button" data-testid="price-calc-qty-plus" onClick={() => setQty((q) => Math.min(10, +(q + 0.2).toFixed(1)))}
                      className="px-2 py-1.5 border border-industrial text-muted-industrial hover:text-primary-orange mono text-sm">+</button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-1">
                  <button type="button" onClick={() => run("inside")} data-testid="price-calc-inside-btn"
                    className={`border px-2 py-2 mono text-[9px] uppercase tracking-widest font-black ${
                      calc?.zone === "inside" ? "bg-primary-orange text-black border-primary-orange" : "border-industrial text-ink hover:border-primary-orange hover:text-primary-orange"
                    }`}>Inside 25km</button>
                  <button type="button" onClick={() => run("outside")} data-testid="price-calc-outside-btn"
                    className={`border px-2 py-2 mono text-[9px] uppercase tracking-widest font-black ${
                      calc?.zone === "outside" ? "bg-primary-orange text-black border-primary-orange" : "border-industrial text-ink hover:border-primary-orange hover:text-primary-orange"
                    }`}>Outside 25km</button>
                </div>

                {askKm && (
                  <div className="border border-primary-orange/60 p-2 space-y-2" data-testid="price-calc-km-prompt">
                    <div className="mono text-[9px] uppercase tracking-widest text-primary-orange">Km beyond 25km?</div>
                    <div className="flex gap-1">
                      <input type="number" step="0.1" inputMode="decimal" autoFocus value={kmInput}
                        data-testid="price-calc-km-input"
                        onChange={(e) => setKmInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && confirmKm()}
                        placeholder="e.g. 12"
                        className="flex-1 rounded-none bg-transparent border border-industrial px-2 py-1.5 mono text-sm text-ink outline-none focus:border-primary-orange" />
                      <button type="button" onClick={confirmKm} data-testid="price-calc-km-apply"
                        className="mono text-[9px] uppercase tracking-widest font-black px-3 bg-primary-orange text-black">Apply</button>
                    </div>
                  </div>
                )}

                {result && (
                  <div className="border-t border-industrial pt-2 space-y-1" data-testid="price-calc-result">
                    <div className="flex justify-between mono text-[10px] text-muted-industrial">
                      <span>{qty.toFixed(1)} m³ × {money(result.per)}</span><span>{money(result.concrete)}</span>
                    </div>
                    {result.cartage > 0 && (
                      <div className="flex justify-between mono text-[10px] text-muted-industrial" data-testid="price-calc-cartage-line">
                        <span>Cartage {result.extraKm} km × {money(cfg.cartage_per_km)}</span><span>{money(result.cartage)}</span>
                      </div>
                    )}
                    {result.shortLoad > 0 && (
                      <div className="flex justify-between mono text-[10px] text-amber-500" data-testid="price-calc-shortload-line">
                        <span>Small load · {result.shortfallM3} m³ under {result.minM3}m³ × {money(smallLoad.min_load_charge)}</span><span>{money(result.shortLoad)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline pt-1">
                      <span className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Total ex GST</span>
                      <span className="heading text-xl font-black text-primary-orange" data-testid="price-calc-total-ex">{money(result.ex)}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Inc GST</span>
                      <span className="heading text-base font-black text-emerald-500" data-testid="price-calc-total-inc">{money(result.inc)}</span>
                    </div>
                  </div>
                )}

                <button type="button" onClick={clearAll} data-testid="price-calc-clear-btn"
                  className="w-full mono text-[9px] uppercase tracking-widest px-2 py-1.5 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange inline-flex items-center justify-center gap-1">
                  <RotateCcw className="w-3 h-3" /> Clear
                </button>
              </>
            )}
          </div>
        </div>
      ), document.body)}
    </>
  );
}
